import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, effect, HostListener, OnDestroy, OnInit, Renderer2, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { LoadingBarModule, LoadingBarService } from '@ngx-loading-bar/core';
import { MDBModalRef } from '../app/shared/services/mdb-modal-stub.service';
import { environment } from '../environments/environment';
import { AppConfigService } from './core/config/app-config.service';
import { SeoService } from './core/services/seo.service';
import { AppInsightsService } from './core/services/app-insights.service';
import { FooterComponent } from './core/footer/footer.component';
import { HeaderComponent } from './core/header/header.component';
import { OverlayComponent } from './core/overlay/overlay.component';
import { EventBusService } from './core/services/event-bus.service';
import { GoogleService } from './core/services/google.service';
import { Constants } from './shared/models/constants';
import { GototopComponent } from './shared/widgets/gototop/gototop.component';
import { ToastContainerComponent } from './shared/widgets/toast-container/toast-container.component';

declare var gtag: Function;

@Component({
    selector: 'app-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [
        RouterOutlet,
        LoadingBarModule,
        HeaderComponent,
        FooterComponent,
        OverlayComponent,
        GototopComponent,
        ToastContainerComponent,
    ]
})
export class AppComponent implements OnInit, OnDestroy {
  overlayDelay = environment.overlayDelay;
    modalRef: MDBModalRef;
     baseUrl: string = environment.API_URL;
    serviceBase = this.baseUrl + '/api/account';
    queryString: string;
    state: any;
    private readonly _httpRequests = toSignal(this.eventBus.httpRequests$);
    private readonly _httpResponses = toSignal(this.eventBus.httpResponses$);
    private readonly _routerEvents = toSignal(this.router.events);
    enabled = signal(false);
    queue = signal<any[]>([]);
    constants = Constants;
    loadDelay = 1000;
    progressValue = signal(0);
    startFromZero = signal(false);
    initialLoaded = true;
    currentProgress = 0;
    private isLoadingBarStarted = false;
    private queueStuckTimeout: any;

    private resetQueueWatchdog(): void {
        if (this.queueStuckTimeout) {
            clearTimeout(this.queueStuckTimeout);
        }
        if (untracked(() => this.queue()).length > 0) {
            this.queueStuckTimeout = setTimeout(() => {
                if (untracked(() => this.queue()).length > 0) {
                    console.warn('[AppComponent] Queue watchdog triggered - force clearing loader state');
                    this.queue.set([]);
                    this.isLoadingBarStarted = false;
                    this.loadingBar.complete();
                    this.enabled.set(false);
                    document.body.classList.add('removeOverflowHidden');
                }
            }, 30000);
        }
    }
    
    @HostListener('window:resize', ['$event'])
    onResize(event) {
        // event.target.innerWidth;
        if (event.target.innerWidth >= 768) {
            this.renderer.addClass(document.body, 'sidemenu-collapsed');
        } else {
            console.log('Else');
            this.renderer.removeClass(document.body, 'sidemenu-collapsed');
        }
    }

    constructor(
      private renderer: Renderer2,
      private googleService: GoogleService,
      private http: HttpClient,
        private router: Router,
        private loadingBar: LoadingBarService,
        private eventBus: EventBusService,
        private seoService: SeoService,
        private appInsightsService: AppInsightsService,
        private appConfigService: AppConfigService,
    ) {
        effect(() => {
            if (this.appConfigService.isLoaded()) {
                untracked(() => {
                    this.seoService.initialize();
                    this.googleService.initializeAnalytics(this.renderer);
                    this.appInsightsService.initialize();
                });
            }
        });
        if (typeof gtag !== 'undefined') {
            gtag('event', 'EDGEApp init', { event_label: 'EDGEApp Init' });
        }
        effect(() => {
            const event = this._routerEvents();
            if (event instanceof NavigationStart) {
                if (!!event.url && event.url.match(/^\/#/)) {
                    this.router.navigate([event.url.replace('/#', '')]);
                }
            }
        });
        effect(() => {
            const data = this._httpRequests();
            if (data != null) {
                if (this.constants.shouldTrackOverlay(data.url)) {
                    this.queue.update(q => [...q, {}]);
                    if (!this.isLoadingBarStarted) {
                        this.isLoadingBarStarted = true;
                        this.loadingBar.start();
                    }
                    this.resetQueueWatchdog();
                    if (untracked(() => this.queue()).length === 1) {
                        setTimeout(() => {
                            if (this.queue().length) {
                                if (this.progressValue() == 0) {
                                    this.startFromZero.set(true);
                                }
                                this.enabled.set(true); document.body.classList.remove('removeOverflowHidden');
                            }
                        }, this.overlayDelay);
                    }
                }
            }
        });
        effect(() => {
            const responseEvent = this._httpResponses();
            if (responseEvent != null) {
                const responseUrl = responseEvent?.value?.url as string;
                const isTrackedResponse = responseEvent?.value?.tracked ?? this.constants.shouldTrackOverlay(responseUrl);
                if (isTrackedResponse && untracked(() => this.queue()).length > 0) {
                    this.queue.update(q => { const n = [...q]; n.pop(); return n; });
                }
                this.resetQueueWatchdog();
                
                if (untracked(() => this.queue()).length === 0) {
                    setTimeout(() => {
                        if (this.queue().length === 0) {
                            this.isLoadingBarStarted = false;
                            this.loadingBar.complete();
                            this.enabled.set(false);
                            document.body.classList.add('removeOverflowHidden');
                        }
                    }, this.overlayDelay);
                }
            }
        });
    }

    ngOnInit() {
        if (window.innerWidth >= 768) {
            this.renderer.addClass(document.body, 'sidemenu-collapsed');
        }
    }

    ngOnDestroy() {
        if (this.queueStuckTimeout) {
            clearTimeout(this.queueStuckTimeout);
        }
        if (window.innerWidth >= 768) {
            this.renderer.removeClass(document.body, 'sidemenu-collapsed');
        }
    }

    //public doBeforeUnload(): void {
    //    this.queryString = '/DeleteSessionFiles';
    //    this.http.post(this.serviceBase + this.queryString, null);       
    //}
}
